import React, { useContext, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Button } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import MergeTypeRoundedIcon from "@mui/icons-material/MergeTypeRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import SoupKitchenRoundedIcon from "@mui/icons-material/SoupKitchenRounded";
import LocalLaundryServiceRoundedIcon from "@mui/icons-material/LocalLaundryServiceRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";

import Spinner from "../../Components/Spinner/Spinner";
import ErrorDialogue from "../../Components/ErrorDialogue/ErrorDialogue";
import AreYouSureDialog from "../../Components/Dialogs/AreYouSureDialog";
import EditWorkerDialog, { EditWorkerDialogResult } from "../../Components/Dialogs/EditWorkerDialog";
import MergeWorkerDialog from "../../Components/Dialogs/MergeWorkerDialog";
import UserDetailsDialog from "../../Components/UserDetailsDialog/UserDetailsDialog";
import WorkerEarnedTipPerMonth from "../../Components/WorkerDetails/WorkerEarnedTipPerMonth";
import WorkerRepository from "../../Repositories/WorkerRepository";
import LocationRepository from "../../Repositories/LocationRepository";
import WorkerData from "../../Types/WorkerData";
import WorkerGetResponse from "../../Repositories/WorkerResponses/WorkerGetResponse";
import WorkerGetCountPeriodTipHistoryResponse from "../../Repositories/WorkerResponses/WorkerGetCountPeriodTipHistoryResponse";
import WorkerWorkedDayResponse from "../../Repositories/WorkerResponses/WorkerWorkedDayResponse";
import { AuthContext } from "../../Types/AuthContext";
import { Functions } from "../../Types/Enums/Functions";
import { Permissions } from "../../Types/Enums/Permissions";

type RangePreset = "6m" | "12m" | "ytd";

const ACCENT = "#c79656";
const SURFACE = "#ffffff";
const BORDER = "rgba(0,0,0,0.07)";
const TEXT_PRIMARY = "#18181b";
const TEXT_SECONDARY = "rgba(24,24,27,0.52)";

function getRangePreset(preset: RangePreset) {
    const endDate = dayjs().endOf("month");
    if (preset === "6m") {
        return { label: "Last 6 months", startDate: dayjs().subtract(5, "month").startOf("month"), endDate };
    }
    if (preset === "ytd") {
        return { label: "This year", startDate: dayjs().startOf("year"), endDate };
    }
    return { label: "Last 12 months", startDate: dayjs().subtract(11, "month").startOf("month"), endDate };
}

function formatCurrency(amount: number) {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(amount || 0);
}

function formatDateRange(startDate: Dayjs, endDate: Dayjs) {
    return `${startDate.format("MMM YYYY")} – ${endDate.format("MMM YYYY")}`;
}

function mapWorkerResponseToData(worker: WorkerGetResponse, username: string) {
    const nextWorker = new WorkerData(worker.id, worker.userId ?? "", worker.firstName, worker.lastName, worker.debt, 0, worker.permissions, worker.function, 0, 0);
    nextWorker.Username = username;
    return nextWorker;
}

function cloneWorker(worker: WorkerData) {
    const nextWorker = new WorkerData(worker.Guid, worker.UserId ?? "", worker.FirstName, worker.LastName, worker.Debt, worker.RoundedTip, worker.Permission, worker.Function, worker.CashTip, worker.PinTip);
    nextWorker.Username = worker.Username;
    nextWorker.TipStatus = worker.TipStatus;
    nextWorker.PreviousTipsReceived = worker.PreviousTipsReceived;
    return nextWorker;
}

function functionMeta(func: Functions) {
    switch (func) {
        case Functions.Server: return { label: "Server", Icon: RestaurantRoundedIcon };
        case Functions.Kitchen: return { label: "Kitchen", Icon: SoupKitchenRoundedIcon };
        case Functions.Dishwashing: return { label: "Dishwashing", Icon: LocalLaundryServiceRoundedIcon };
        default: return { label: "Unknown", Icon: RestaurantRoundedIcon };
    }
}

function permissionMeta(permission: Permissions) {
    switch (permission) {
        case Permissions.Administrator: return { label: "Admin", Icon: AdminPanelSettingsRoundedIcon };
        case Permissions.Developer: return { label: "Developer", Icon: CodeRoundedIcon };
        case Permissions.User:
        default: return { label: "User", Icon: PersonOutlineRoundedIcon };
    }
}

export default function WorkerDetailsPage() {
    const auth = useContext(AuthContext);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const workerId = searchParams.get("workerId") ?? "";
    const [rangePreset, setRangePreset] = useState<RangePreset>("12m");
    const [worker, setWorker] = useState<WorkerData | null>(null);
    const [locationName, setLocationName] = useState("");
    const [tipHistory, setTipHistory] = useState<WorkerGetCountPeriodTipHistoryResponse>({ cashCountPeriods: [], pinCountPeriods: [] });
    const [workedDays, setWorkedDays] = useState<WorkerWorkedDayResponse[]>([]);
    const [isHeaderLoading, setIsHeaderLoading] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [isMergeLoading, setIsMergeLoading] = useState(false);
    const [isSavingWorker, setIsSavingWorker] = useState(false);
    const [showEditWorkerDialog, setShowEditWorkerDialog] = useState(false);
    const [showAccountDialog, setShowAccountDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [mergeCandidates, setMergeCandidates] = useState<WorkerData[]>([]);
    const [errorState, setErrorState] = useState({ show: false, title: "", message: "" });

    const selectedRange = getRangePreset(rangePreset);
    const totalTip = tipHistory.cashCountPeriods.reduce((sum, period) => sum + (period.cashTip ?? 0), 0)
        + tipHistory.pinCountPeriods.reduce((sum, period) => sum + (period.pinTip ?? 0), 0);
    const totalWorkedDays = workedDays.length;
    const totalWorkPeriods = workedDays.reduce((sum, workDay) => sum + workDay.workPeriods.length, 0);
    const hasLinkedAccount = !!worker?.Username;

    useEffect(() => {
        let isCancelled = false;

        async function loadWorkerHeader() {
            if (!workerId) { setWorker(null); setLocationName(""); return; }
            setIsHeaderLoading(true);
            const [workerResult, locationResult, usernamesResult] = await Promise.all([
                WorkerRepository.getWorkerById(auth.locationId, workerId),
                LocationRepository.getLocationName(auth.locationId),
                LocationRepository.getUsernames(auth.locationId),
            ]);
            if (isCancelled) return;
            const failedResult = [workerResult, locationResult, usernamesResult].find((result) => result.isFailed());
            if (failedResult) {
                setErrorState({ show: true, title: failedResult.errorTitle ?? "Failed to load worker", message: failedResult.errorMessage ?? "The worker details could not be loaded." });
                setIsHeaderLoading(false);
                return;
            }
            const nextWorker = mapWorkerResponseToData(workerResult.result!, usernamesResult.result?.users[workerResult.result!.id] ?? "");
            setWorker(nextWorker);
            setShowEditWorkerDialog(false);
            setLocationName(locationResult.result ?? "");
            setIsHeaderLoading(false);
        }

        loadWorkerHeader();
        return () => { isCancelled = true; };
    }, [auth.locationId, workerId]);

    useEffect(() => {
        let isCancelled = false;

        async function loadWorkerHistory() {
            if (!workerId) { setTipHistory({ cashCountPeriods: [], pinCountPeriods: [] }); setWorkedDays([]); return; }
            const range = getRangePreset(rangePreset);
            setIsHistoryLoading(true);
            const [tipHistoryResult, workedDaysResult] = await Promise.all([
                WorkerRepository.getWorkerCountPeriodTipHistory(auth.locationId, workerId, range.startDate, range.endDate),
                WorkerRepository.getWorkerWorkedDays(auth.locationId, workerId, range.startDate, range.endDate),
            ]);
            if (isCancelled) return;
            const failedResult = [tipHistoryResult, workedDaysResult].find((result) => result.isFailed());
            if (failedResult) {
                setErrorState({ show: true, title: failedResult.errorTitle ?? "Failed to load worker history", message: failedResult.errorMessage ?? "The worker history could not be loaded." });
                setIsHistoryLoading(false);
                return;
            }
            setTipHistory(tipHistoryResult.result ?? { cashCountPeriods: [], pinCountPeriods: [] });
            setWorkedDays(workedDaysResult.result?.response ?? []);
            setIsHistoryLoading(false);
        }

        loadWorkerHistory();
        return () => { isCancelled = true; };
    }, [auth.locationId, workerId, rangePreset]);

    async function onCloseEditWorkerDialog(updatedWorker: EditWorkerDialogResult | null) {
        if (updatedWorker == null || worker == null) {
            setShowEditWorkerDialog(false);
            return;
        }

        setIsSavingWorker(true);

        const result = await WorkerRepository.updateWorker(auth.locationId, worker.Guid, {
            FirstName: updatedWorker.firstName,
            LastName: updatedWorker.lastName,
            Function: updatedWorker.function,
        });

        if (result.isFailed()) {
            setErrorState({ show: true, title: result.errorTitle ?? "Failed to update worker", message: result.errorMessage ?? "The worker could not be updated." });
            setIsSavingWorker(false);
            return;
        }

        setWorker((current) => current == null ? current : {
            ...current,
            FirstName: updatedWorker.firstName,
            LastName: updatedWorker.lastName,
            Function: updatedWorker.function,
        });
        setShowEditWorkerDialog(false);
        setIsSavingWorker(false);
    }

    async function onDeleteWorker(accepted: boolean) {
        if (!accepted || worker == null) { setShowDeleteDialog(false); return; }
        const result = await WorkerRepository.deleteWorker(auth.locationId, worker.Guid);
        if (result.isFailed()) {
            setErrorState({ show: true, title: result.errorTitle ?? "Failed to delete worker", message: result.errorMessage ?? "The worker could not be deleted." });
            setShowDeleteDialog(false);
            return;
        }
        setShowDeleteDialog(false);
        navigate("/organisation/team");
    }

    async function onOpenMergeDialog() {
        if (worker == null) return;
        setIsMergeLoading(true);

        const [workersResult, usernamesResult] = await Promise.all([
            WorkerRepository.getAllWorkers(auth.locationId),
            LocationRepository.getUsernames(auth.locationId),
        ]);

        const failedResult = [workersResult, usernamesResult].find((result) => result.isFailed());
        if (failedResult) {
            setErrorState({ show: true, title: failedResult.errorTitle ?? "Failed to load workers", message: failedResult.errorMessage ?? "The workers could not be loaded." });
            setIsMergeLoading(false);
            return;
        }

        const nextCandidates = workersResult.result!.response.map((candidate) =>
            mapWorkerResponseToData(candidate, usernamesResult.result?.users[candidate.id] ?? "")
        );

        setMergeCandidates(nextCandidates);
        setShowMergeDialog(true);
        setIsMergeLoading(false);
    }

    async function onMergeWorker(mergeIntoWorkerId: string | null) {
        if (mergeIntoWorkerId == null || worker == null) {
            setShowMergeDialog(false);
            return;
        }

        setIsMergeLoading(true);
        const result = await WorkerRepository.mergeWorkers(auth.locationId, worker.Guid, mergeIntoWorkerId);
        if (result.isFailed()) {
            setErrorState({ show: true, title: result.errorTitle ?? "Failed to merge worker", message: result.errorMessage ?? "The worker could not be merged." });
            setShowMergeDialog(false);
            setIsMergeLoading(false);
            return;
        }

        const targetWorkerId = result.result?.mergedIntoWorkerId ?? mergeIntoWorkerId;
        setShowMergeDialog(false);
        setIsMergeLoading(false);
        navigate(`/workerdetails?workerId=${encodeURIComponent(targetWorkerId)}`, { replace: true });
    }

    const func = worker ? functionMeta(worker.Function) : null;
    const permission = worker ? permissionMeta(worker.Permission) : null;

    return (
        <div style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "24px 28px", overflowY: "auto", background: "#f8f9fa", position: "relative", fontFamily: "'FunnelDisplay', 'Segoe UI', sans-serif" }}>
            {(isHeaderLoading || isHistoryLoading || isMergeLoading || isSavingWorker) && <Spinner />}

            <ErrorDialogue
                show={errorState.show}
                title={errorState.title}
                message={errorState.message}
                onClose={() => setErrorState({ show: false, title: "", message: "" })}
            />

            {showDeleteDialog && worker != null && (
                <AreYouSureDialog
                    message={`Do you want to delete ${worker.FirstName} ${worker.LastName}?`}
                    onClose={onDeleteWorker}
                />
            )}

            {showAccountDialog && worker != null && (
                <UserDetailsDialog
                    worker={worker}
                    onClose={() => setShowAccountDialog(false)}
                    onUserCreated={(updatedWorker) => setWorker(cloneWorker(updatedWorker))}
                />
            )}

            {showEditWorkerDialog && worker != null && (
                <EditWorkerDialog
                    worker={worker}
                    isSaving={isSavingWorker}
                    onClose={onCloseEditWorkerDialog}
                />
            )}

            {showMergeDialog && worker != null && (
                <MergeWorkerDialog
                    sourceWorker={worker}
                    workers={mergeCandidates}
                    onClose={onMergeWorker}
                />
            )}

            <div style={{ width: "100%", maxWidth: 1600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Top bar */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <Button
                        variant="text"
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate("/organisation/team")}
                        style={{ textTransform: "none", color: TEXT_PRIMARY, fontWeight: 600, borderRadius: 10, paddingInline: 14, paddingBlock: 8, fontSize: 14, background: SURFACE, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
                    >
                        Back to team
                    </Button>

                    <div style={{ display: "flex", gap: 4, padding: 4, background: SURFACE, borderRadius: 12, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                        {(([{ id: "6m", label: "6 months" }, { id: "12m", label: "12 months" }, { id: "ytd", label: "This year" }] as Array<{ id: RangePreset; label: string }>)).map((preset) => {
                            const active = rangePreset === preset.id;
                            return (
                                <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => setRangePreset(preset.id)}
                                    style={{ appearance: "none", borderRadius: 8, border: "none", background: active ? ACCENT : "transparent", color: active ? "#fff" : TEXT_SECONDARY, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease", letterSpacing: 0.1 }}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {!workerId ? (
                    <div style={{ borderRadius: 16, border: `1px solid ${BORDER}`, background: SURFACE, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", padding: 32 }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: -0.4, marginBottom: 8 }}>No worker selected</div>
                        <div style={{ fontSize: 14, color: TEXT_SECONDARY, maxWidth: 480, lineHeight: 1.6 }}>
                            Open a worker from the team page to view the overview, monthly tip history, and worked day history.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Hero + Actions row */}
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: 16, alignItems: "start" }}>

                            {/* Hero card — identity + stats */}
                            <div style={{ borderRadius: 16, border: `1px solid rgba(199,150,86,0.20)`, background: SURFACE, boxShadow: "0 2px 16px rgba(199,150,86,0.10)", padding: 28, display: "flex", flexDirection: "column", gap: 20 }}>

                                {/* Identity */}
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: ACCENT, marginBottom: 8 }}>Worker details</div>
                                    <div style={{ fontSize: 30, fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: -0.8, lineHeight: 1.1 }}>
                                        {worker ? `${worker.FirstName} ${worker.LastName}` : "Loading…"}
                                    </div>
                                    <div style={{ fontSize: 13, color: TEXT_SECONDARY, marginTop: 8, lineHeight: 1.6 }}>
                                        {selectedRange.label} · {formatDateRange(selectedRange.startDate, selectedRange.endDate)}
                                    </div>
                                </div>

                                {/* Meta pills */}
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {func != null && (
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "6px 11px", border: `1px solid ${BORDER}`, background: "#fafafa", color: TEXT_PRIMARY, fontSize: 12, fontWeight: 600 }}>
                                            <func.Icon style={{ fontSize: 15, color: ACCENT }} />
                                            {func.label}
                                        </div>
                                    )}
                                    {permission != null && (
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "6px 11px", border: `1px solid ${BORDER}`, background: "#fafafa", color: TEXT_PRIMARY, fontSize: 12, fontWeight: 600 }}>
                                            <permission.Icon style={{ fontSize: 15, color: TEXT_PRIMARY }} />
                                            {permission.label}
                                        </div>
                                    )}
                                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "6px 11px", border: `1px solid ${BORDER}`, background: "#fafafa", color: TEXT_PRIMARY, fontSize: 12, fontWeight: 600 }}>
                                        <BusinessRoundedIcon style={{ fontSize: 15, color: "#0f766e" }} />
                                        {locationName || "Current location"}
                                    </div>
                                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "6px 11px", border: `1px solid ${BORDER}`, background: "#fafafa", color: TEXT_PRIMARY, fontSize: 12, fontWeight: 600 }}>
                                        <BadgeRoundedIcon style={{ fontSize: 15, color: "#7c3aed" }} />
                                        {hasLinkedAccount ? `@${worker?.Username}` : "No linked account"}
                                    </div>
                                </div>

                                {/* Divider */}
                                <div style={{ height: 1, background: BORDER }} />

                                {/* Stats grid — 4 tiles */}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>

                                    {/* Tip earned */}
                                    <div style={{ borderRadius: 12, border: `1px solid rgba(199,150,86,0.18)`, background: "rgba(199,150,86,0.04)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                                        <EuroRoundedIcon style={{ fontSize: 16, color: ACCENT }} />
                                        <div style={{ fontSize: 20, fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: -0.5, lineHeight: 1 }}>{formatCurrency(totalTip)}</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.3 }}>Tip earned<br /><span style={{ fontWeight: 400, opacity: 0.8 }}>cash & pin</span></div>
                                    </div>

                                    {/* Worked days */}
                                    <div style={{ borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fafafa", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                                        <CalendarMonthRoundedIcon style={{ fontSize: 16, color: ACCENT }} />
                                        <div style={{ fontSize: 20, fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: -0.5, lineHeight: 1 }}>{totalWorkedDays}</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.3 }}>Worked days</div>
                                    </div>

                                    {/* Work periods */}
                                    <div style={{ borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fafafa", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                                        <ScheduleRoundedIcon style={{ fontSize: 16, color: TEXT_PRIMARY }} />
                                        <div style={{ fontSize: 20, fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: -0.5, lineHeight: 1 }}>{totalWorkPeriods}</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.3 }}>Work periods</div>
                                    </div>

                                    {/* Debt */}
                                    <div style={{ borderRadius: 12, border: `1px solid ${BORDER}`, background: "#fafafa", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                                        <AccountBalanceWalletOutlinedIcon style={{ fontSize: 16, color: TEXT_SECONDARY }} />
                                        <div style={{ fontSize: 20, fontWeight: 800, color: TEXT_PRIMARY, letterSpacing: -0.5, lineHeight: 1 }}>{worker ? formatCurrency(worker.Debt) : formatCurrency(0)}</div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, lineHeight: 1.3 }}>Worker debt</div>
                                    </div>

                                </div>
                            </div>

                            {/* Actions panel */}
                            <div style={{ borderRadius: 16, border: `1px solid ${BORDER}`, background: SURFACE, boxShadow: "0 2px 12px rgba(0,0,0,0.05)", padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: TEXT_SECONDARY, marginBottom: 4 }}>Actions</div>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => setShowEditWorkerDialog(true)}
                                    disabled={worker == null}
                                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, paddingInline: 18, paddingBlock: 11, fontSize: 13, borderColor: "rgba(199,150,86,0.40)", color: TEXT_PRIMARY, letterSpacing: 0.1, justifyContent: "flex-start" }}
                                >
                                    Edit worker
                                </Button>

                                <Button
                                    variant="contained"
                                    disableElevation
                                    fullWidth
                                    startIcon={<ManageAccountsRoundedIcon />}
                                    onClick={() => setShowAccountDialog(true)}
                                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, paddingInline: 18, paddingBlock: 11, fontSize: 13, backgroundColor: ACCENT, letterSpacing: 0.1, justifyContent: "flex-start" }}
                                >
                                    {hasLinkedAccount ? "Manage account" : "Create account"}
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<MergeTypeRoundedIcon />}
                                    onClick={onOpenMergeDialog}
                                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, paddingInline: 18, paddingBlock: 11, fontSize: 13, borderColor: "rgba(199,150,86,0.40)", color: TEXT_PRIMARY, letterSpacing: 0.1, justifyContent: "flex-start" }}
                                >
                                    Merge worker
                                </Button>

                                <div style={{ height: 1, background: BORDER }} />

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<DeleteOutlineRoundedIcon />}
                                    onClick={() => setShowDeleteDialog(true)}
                                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 10, paddingInline: 18, paddingBlock: 11, fontSize: 13, borderColor: "rgba(209,24,10,0.25)", color: "#c41a0e", letterSpacing: 0.1, justifyContent: "flex-start" }}
                                >
                                    Delete worker
                                </Button>
                            </div>
                        </div>

                        {/* Charts */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                            <WorkerEarnedTipPerMonth
                                cashCountPeriods={tipHistory.cashCountPeriods}
                                pinCountPeriods={tipHistory.pinCountPeriods}
                                workDays={workedDays}
                                startDate={selectedRange.startDate}
                                endDate={selectedRange.endDate}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
